import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, Pencil, LogOut, Pin, X, Bell, IdCard as IdCardIcon, Eye, Printer, BookOpen, Building2, Users, Settings, Image as ImageIcon, Paperclip, FileText, Library as LibraryIcon, GraduationCap, UsersRound, CalendarDays, ClipboardList, Table, Award, ScrollText, Images, Inbox, BookText, Check, Ban } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/api";
import IdCardVisual from "../components/IdCardVisual";
import ListManager from "../components/admin/ListManager";
import SettingsEditor from "../components/admin/SettingsEditor";
import GalleryManager from "../components/admin/GalleryManager";
import TeamManager from "../components/admin/TeamManager";
import StudentRecordsManager from "../components/admin/StudentRecordsManager";
import RoutineEditor from "../components/admin/RoutineEditor";
import MarksheetManager from "../components/admin/MarksheetManager";
import SyllabusManager from "../components/admin/SyllabusManager";
import ProspectusManager from "../components/admin/ProspectusManager";
import ApplicationsManager from "../components/admin/ApplicationsManager";
import SlidesManager from "../components/admin/SlidesManager";
import LibraryManager from "../components/admin/LibraryManager";

const CATEGORIES = ["General", "Admission", "Academic", "Examination", "Event"];
const emptyNotice = { title: "", body: "", category: "General", pinned: false, attachment: "", attachment_type: "", attachment_name: "" };

function readImage(file, maxW) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject; img.src = e.target.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}
function readFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject; r.readAsDataURL(file);
  });
}

export default function Admin() {
  const { user, logout } = useAuth();
  const { courses, departments, students, gallery, subjects, team, semesters, records, marksheets, syllabus, prospectus, slides, settings } = useSite();
  const navigate = useNavigate();
  const [tab, setTab] = useState("notices");

  const [notices, setNotices] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyNotice);
  const [saving, setSaving] = useState(false);

  const [cards, setCards] = useState([]);
  const [cardFilter, setCardFilter] = useState("all");
  const [viewCard, setViewCard] = useState(null);
  const [rejectCard, setRejectCard] = useState(null);
  const [rejectRemark, setRejectRemark] = useState("");

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const loadNotices = () => api.listNotices().then((r) => setNotices(r.data));
  const loadCards = () => api.listIdCards().then((r) => setCards(r.data)).catch(() => {});
  useEffect(() => { loadNotices(); loadCards(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyNotice); setModal(true); };
  const openEdit = (n) => { setEditing(n.id); setForm({ title: n.title, body: n.body, category: n.category, pinned: n.pinned, attachment: n.attachment || "", attachment_type: n.attachment_type || "", attachment_name: n.attachment_name || "" }); setModal(true); };

  const onNoticeFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      if (file.type === "application/pdf") {
        const data = await readFile(file);
        setForm((f) => ({ ...f, attachment: data, attachment_type: "pdf", attachment_name: file.name }));
      } else if (file.type.startsWith("image/")) {
        const data = await readImage(file, 1200);
        setForm((f) => ({ ...f, attachment: data, attachment_type: "image", attachment_name: file.name }));
      } else {
        toast.error("Only image or PDF files are allowed");
        return;
      }
      toast.success("Attachment added");
    } catch { toast.error("Could not read file"); }
  };
  const removeAttachment = () => setForm((f) => ({ ...f, attachment: "", attachment_type: "", attachment_name: "" }));

  const saveNotice = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.updateNotice(editing, form); toast.success("Notice updated"); }
      else { await api.createNotice(form); toast.success("Notice published"); }
      setModal(false);
      await loadNotices();
    } catch { toast.error("Failed to save notice"); }
    finally { setSaving(false); }
  };

  const removeNotice = async (id) => {
    try { await api.deleteNotice(id); toast.success("Notice deleted"); await loadNotices(); }
    catch { toast.error("Failed to delete"); }
  };

  const removeCard = async (id) => {
    try { await api.deleteIdCard(id); toast.success("Record deleted"); await loadCards(); }
    catch { toast.error("Failed to delete"); }
  };

  const setCardApproval = async (c, approve) => {
    try { await api.setCardActive(c.id, approve); toast.success(approve ? "ID card approved" : "ID card revoked"); await loadCards(); }
    catch { toast.error("Failed to update status"); }
  };

  const submitReject = async () => {
    if (!rejectRemark.trim()) { toast.error("Please add a reason for rejection"); return; }
    try {
      const { data } = await api.rejectIdCard(rejectCard.id, rejectRemark.trim());
      toast.success(`ID card rejected${data.emailed ? " & applicant emailed" : ""}`);
      setRejectCard(null); setRejectRemark("");
      await loadCards();
    } catch { toast.error("Failed to reject"); }
  };

  if (!user) return <div className="min-h-screen bg-bone flex items-center justify-center text-muted">Loading…</div>;

  const TabBtn = ({ id, icon: Icon, label, count }) => (
    <button data-testid={`tab-${id}`} onClick={() => setTab(id)} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest transition-colors ${tab === id ? "bg-gold text-forest" : "border border-bone/25 text-bone hover:bg-bone/10"}`}>
      <Icon size={15} /> {label} {count != null && <span className="opacity-70">({count})</span>}
    </button>
  );

  return (
    <div data-testid="admin-page" className="min-h-screen bg-bone">
      <div className="bg-forest text-bone">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Administrator</p>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Control Panel</h1>
              <p className="text-bone/50 text-sm mt-2">{user.email}</p>
            </div>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 border border-bone/25 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-bone hover:text-forest transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
          <div className="flex gap-3 mt-8 flex-wrap">
            <TabBtn id="notices" icon={Bell} label="Notices" count={notices.length} />
            <TabBtn id="cards" icon={IdCardIcon} label="ID Cards" count={cards.length} />
            <TabBtn id="applications" icon={Inbox} label="Applications" count={null} />
            <TabBtn id="slides" icon={Images} label="Home Slides" count={slides.length} />
            <TabBtn id="courses" icon={BookOpen} label="Courses" count={courses.length} />
            <TabBtn id="departments" icon={Building2} label="Departments" count={departments.length} />
            <TabBtn id="students" icon={Users} label="Student Items" count={students.length} />
            <TabBtn id="subjects" icon={GraduationCap} label="Subjects" count={subjects.length} />
            <TabBtn id="semesters" icon={CalendarDays} label="Semesters" count={semesters.length} />
            <TabBtn id="syllabus" icon={ScrollText} label="Syllabus" count={syllabus.length} />
            <TabBtn id="prospectus" icon={BookText} label="Prospectus" count={prospectus.length} />
            <TabBtn id="records" icon={ClipboardList} label="Student Records" count={records.length} />
            <TabBtn id="marksheets" icon={Award} label="Marksheets" count={marksheets.length} />
            <TabBtn id="routine" icon={Table} label="Routine" count={null} />
            <TabBtn id="team" icon={UsersRound} label="Employees" count={team.length} />
            <TabBtn id="gallery" icon={ImageIcon} label="Gallery" count={gallery.length} />
            <TabBtn id="library" icon={LibraryIcon} label="Library" count={null} />
            <TabBtn id="content" icon={Settings} label="Site Content" count={null} />
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12">
        {tab === "notices" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-2xl text-forest">{notices.length} Notice{notices.length !== 1 ? "s" : ""}</h2>
              <button data-testid="add-notice-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
                <Plus size={16} /> New Notice
              </button>
            </div>
            <div className="border border-line divide-y divide-line bg-white">
              {notices.map((n) => (
                <div key={n.id} data-testid="admin-notice-row" className="flex items-start justify-between gap-6 p-6">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {n.pinned && <Pin size={14} className="text-gold" />}
                      <h3 className="font-serif text-xl text-forest">{n.title}</h3>
                      <span className="text-xs uppercase tracking-widest text-gold border border-gold/40 px-2 py-0.5 rounded-full">{n.category}</span>
                    </div>
                    <p className="text-muted text-sm mt-2 max-w-2xl line-clamp-2">{n.body}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid="edit-notice-btn" onClick={() => openEdit(n)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone transition-colors"><Pencil size={15} /></button>
                    <button data-testid="delete-notice-btn" onClick={() => removeNotice(n.id)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
              {notices.length === 0 && <p className="p-8 text-muted">No notices yet.</p>}
            </div>
          </>
        )}

        {tab === "cards" && (
          <>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <h2 className="font-serif text-2xl text-forest">{cards.length} ID Card Record{cards.length !== 1 ? "s" : ""}</h2>
              <Link to="/id-card" data-testid="admin-new-card" className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
                <Plus size={16} /> New ID Card
              </Link>
            </div>
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              {[["all", "All"], ["student", "Students"], ["employee", "Employees"]].map(([v, l]) => (
                <button key={v} data-testid={`card-filter-${v}`} onClick={() => setCardFilter(v)} className={`text-xs px-4 py-1.5 rounded-full border transition-colors ${cardFilter === v ? "bg-forest text-bone border-forest" : "border-line text-muted hover:border-forest"}`}>
                  {l} {v !== "all" && <span className="opacity-60">({cards.filter((c) => c.type === v).length})</span>}
                </button>
              ))}
            </div>
            {(() => { const shownCards = cardFilter === "all" ? cards : cards.filter((c) => c.type === cardFilter); return (
            <div className="border border-line bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-bone text-forest/60 uppercase text-xs tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">Card No.</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-left px-4 py-3">Department</th>
                    <th className="text-left px-4 py-3">Phone</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {shownCards.map((c) => (
                    <tr key={c.id} data-testid="admin-card-row">
                      <td className="px-4 py-3 font-mono text-forest">{c.card_no}</td>
                      <td className="px-4 py-3 font-medium">{c.name}</td>
                      <td className="px-4 py-3"><span className="text-xs uppercase tracking-widest text-gold">{c.type}</span></td>
                      <td className="px-4 py-3 text-muted">{c.department}</td>
                      <td className="px-4 py-3 text-muted">{c.phone}</td>
                      <td className="px-4 py-3">
                        {c.active !== false ? (
                          <span data-testid="card-status-badge" className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-forest/10 text-forest border border-forest/30"><Check size={13} /> Approved</span>
                        ) : c.rejected ? (
                          <span data-testid="card-status-badge" title={c.remark || ""} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-destructive/10 text-destructive border border-destructive/30"><Ban size={13} /> Rejected</span>
                        ) : (
                          <span data-testid="card-status-badge" className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          {c.active !== false ? (
                            <button data-testid="revoke-card-btn" title="Revoke approval" onClick={() => setCardApproval(c, false)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-destructive/30 text-destructive rounded-sm hover:bg-destructive hover:text-white transition-colors"><Ban size={14} /> Revoke</button>
                          ) : (
                            <>
                              <button data-testid="approve-card-btn" title="Approve ID card" onClick={() => setCardApproval(c, true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-forest/40 text-forest rounded-sm hover:bg-forest hover:text-bone transition-colors"><Check size={14} /> Approve</button>
                              {!c.rejected && (
                                <button data-testid="reject-card-btn" title="Reject with remark" onClick={() => { setRejectCard(c); setRejectRemark(c.remark || ""); }} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-destructive/30 text-destructive rounded-sm hover:bg-destructive hover:text-white transition-colors"><Ban size={14} /> Reject</button>
                              )}
                            </>
                          )}
                          <button data-testid="view-card-btn" onClick={() => setViewCard(c)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone transition-colors"><Eye size={15} /></button>
                          <button data-testid="delete-card-btn" onClick={() => removeCard(c.id)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white transition-colors"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shownCards.length === 0 && <p className="p-8 text-muted">No {cardFilter !== "all" ? cardFilter : "ID card"} registrations yet.</p>}
            </div>
            ); })()}
          </>
        )}

        {tab === "courses" && (
          <ListManager
            resource="courses" testid="course" title="Courses" items={courses}
            fields={[
              { key: "code", label: "Code", type: "text" },
              { key: "title", label: "Title", type: "text" },
              { key: "years", label: "Duration", type: "text" },
              { key: "desc", label: "Description", type: "textarea" },
              { key: "subjects", label: "Subjects", type: "list" },
            ]}
            display={(c) => (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-xs text-gold">{c.code}</span>
                  <h3 className="font-serif text-xl text-forest">{c.title}</h3>
                  <span className="text-xs uppercase tracking-widest text-forest/50">{c.years}</span>
                </div>
                <p className="text-muted text-sm mt-2 max-w-2xl line-clamp-2">{c.desc}</p>
              </>
            )}
          />
        )}

        {tab === "departments" && (
          <ListManager
            resource="departments" testid="department" title="Departments" items={departments}
            fields={[
              { key: "name", label: "Name", type: "text" },
              { key: "university", label: "University", type: "text" },
              { key: "desc", label: "Description", type: "textarea" },
              { key: "partner", label: "Partner / Collaboration (highlight)", type: "checkbox" },
            ]}
            display={(d) => (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif text-xl text-forest">{d.name}</h3>
                  <span className="text-xs uppercase tracking-widest text-gold border border-gold/40 px-2 py-0.5 rounded-full">{d.university}</span>
                </div>
                <p className="text-muted text-sm mt-2 max-w-2xl line-clamp-2">{d.desc}</p>
              </>
            )}
          />
        )}

        {tab === "students" && (
          <ListManager
            resource="students" testid="student-item" title="Student Items" items={students}
            fields={[
              { key: "title", label: "Title", type: "text" },
              { key: "desc", label: "Description", type: "textarea" },
            ]}
            display={(s) => (
              <>
                <h3 className="font-serif text-xl text-forest">{s.title}</h3>
                <p className="text-muted text-sm mt-2 max-w-2xl line-clamp-2">{s.desc}</p>
              </>
            )}
          />
        )}

        {tab === "subjects" && (
          <ListManager
            resource="subjects" testid="subject" title="Subjects" items={subjects}
            fields={[
              { key: "name", label: "Subject Name", type: "text" },
              { key: "category", label: "Category", type: "select", options: ["Major", "Minor", "Skill", "Value-Added"] },
              { key: "desc", label: "Description", type: "textarea" },
            ]}
            display={(s) => (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif text-xl text-forest">{s.name}</h3>
                  <span className="text-xs uppercase tracking-widest text-gold border border-gold/40 px-2 py-0.5 rounded-full">{s.category}</span>
                </div>
                <p className="text-muted text-sm mt-2 max-w-2xl line-clamp-2">{s.desc}</p>
              </>
            )}
          />
        )}

        {tab === "semesters" && (
          <ListManager
            resource="semesters" testid="semester" title="Semesters" items={semesters}
            fields={[
              { key: "semester", label: "Semester", type: "select", options: ["Semester I", "Semester II", "Semester III", "Semester IV", "Semester V", "Semester VI", "Semester VII", "Semester VIII"] },
              { key: "program", label: "Programme", type: "text" },
              { key: "subjects", label: "Subjects / Papers", type: "list" },
            ]}
            display={(s) => (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif text-xl text-forest">{s.semester}</h3>
                  {s.program && <span className="text-xs uppercase tracking-widest text-gold border border-gold/40 px-2 py-0.5 rounded-full">{s.program}</span>}
                </div>
                <p className="text-muted text-sm mt-2 max-w-2xl line-clamp-2">{(s.subjects || []).join(", ")}</p>
              </>
            )}
          />
        )}

        {tab === "team" && <TeamManager />}

        {tab === "records" && <StudentRecordsManager />}

        {tab === "marksheets" && <MarksheetManager />}

        {tab === "syllabus" && <SyllabusManager />}
        {tab === "prospectus" && <ProspectusManager />}

        {tab === "applications" && <ApplicationsManager />}

        {tab === "slides" && <SlidesManager />}

        {tab === "routine" && <RoutineEditor />}

        {tab === "gallery" && <GalleryManager />}

        {tab === "library" && <LibraryManager />}

        {tab === "content" && <SettingsEditor />}
      </div>


      {/* Notice modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <form data-testid="notice-form" onClick={(e) => e.stopPropagation()} onSubmit={saveNotice} className="bg-bone w-full max-w-lg rounded-sm border border-line p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-3xl text-forest">{editing ? "Edit Notice" : "New Notice"}</h3>
              <button type="button" onClick={() => setModal(false)} className="text-muted hover:text-forest"><X size={22} /></button>
            </div>
            <label className="block text-xs uppercase tracking-widest text-forest/60 mb-2">Title</label>
            <input data-testid="notice-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="w-full bg-white border border-line focus:border-gold outline-none px-4 py-3 rounded-sm mb-5 focus:ring-2 focus:ring-gold/30" />
            <label className="block text-xs uppercase tracking-widest text-forest/60 mb-2">Body</label>
            <textarea data-testid="notice-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required rows={4} className="w-full bg-white border border-line focus:border-gold outline-none px-4 py-3 rounded-sm mb-5 focus:ring-2 focus:ring-gold/30 resize-none" />
            <label className="block text-xs uppercase tracking-widest text-forest/60 mb-2">Category</label>
            <select data-testid="notice-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-white border border-line focus:border-gold outline-none px-4 py-3 rounded-sm mb-5">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="flex items-center gap-3 mb-6 cursor-pointer">
              <input data-testid="notice-pinned-checkbox" type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} className="w-4 h-4 accent-forest" />
              <span className="text-sm text-forest">Pin to top of notice board</span>
            </label>

            <label className="block text-xs uppercase tracking-widest text-forest/60 mb-2">Attachment (poster image or PDF)</label>
            {form.attachment ? (
              <div className="flex items-center gap-3 mb-6 border border-line rounded-sm p-3 bg-white">
                {form.attachment_type === "image" ? (
                  <img src={form.attachment} alt="attachment" className="w-14 h-14 object-cover rounded" />
                ) : (
                  <span className="w-14 h-14 flex items-center justify-center bg-destructive/10 text-destructive rounded"><FileText size={22} /></span>
                )}
                <span className="text-sm text-forest flex-1 truncate">{form.attachment_name || form.attachment_type}</span>
                <button type="button" data-testid="remove-attachment-btn" onClick={removeAttachment} className="text-muted hover:text-destructive"><X size={18} /></button>
              </div>
            ) : (
              <label className="flex items-center gap-3 mb-6 border border-dashed border-forest/30 rounded-sm px-4 py-3 cursor-pointer hover:border-gold transition-colors">
                <Paperclip size={16} className="text-forest/60" />
                <span className="text-sm text-muted">Attach an image or PDF</span>
                <input data-testid="notice-attachment-input" type="file" accept="image/*,application/pdf" onChange={onNoticeFile} className="hidden" />
              </label>
            )}

            <button data-testid="save-notice-btn" type="submit" disabled={saving} className="w-full bg-forest text-bone py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update Notice" : "Publish Notice"}
            </button>
          </form>
        </div>
      )}

      {/* Card view modal */}
      {viewCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/70 backdrop-blur-sm overflow-y-auto" onClick={() => setViewCard(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-bone rounded-sm border border-line p-6 md:p-10 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 no-print">
              <h3 className="font-serif text-2xl text-forest">{viewCard.card_no}</h3>
              <div className="flex items-center gap-3">
                <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-forest text-bone px-5 py-2.5 rounded-full text-xs uppercase tracking-widest"><Printer size={14} /> Print</button>
                <button onClick={() => setViewCard(null)} className="text-muted hover:text-forest"><X size={22} /></button>
              </div>
            </div>
            <div className="print-area"><IdCardVisual data={viewCard} college={settings.college} logo={settings.images && settings.images.logo} /></div>
          </div>
        </div>
      )}

      {/* Card reject modal */}
      {rejectCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/70 backdrop-blur-sm" onClick={() => setRejectCard(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-sm border border-line p-8">
            <h3 className="font-serif text-2xl text-forest">Reject ID Card</h3>
            <p className="text-muted text-sm mt-2 mb-4">{rejectCard.card_no} — {rejectCard.name}. The reason below is shown to the applicant when they check their status{rejectCard.email ? " and emailed to them" : ""}.</p>
            <textarea data-testid="card-reject-remark" value={rejectRemark} onChange={(e) => setRejectRemark(e.target.value)} rows={3} placeholder="Reason for rejection (e.g. Photo unclear, please re-apply)" className="w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30" />
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setRejectCard(null); setRejectRemark(""); }} className="flex-1 border border-line text-forest py-3 rounded-full text-sm uppercase tracking-widest hover:bg-bone transition-colors">Cancel</button>
              <button data-testid="card-reject-confirm" onClick={submitReject} className="flex-1 bg-destructive text-white py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform">Reject & notify</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
